<!-- =========================================================================================
    File Name: DomainManagement.vue
    Description: Component for managing domains in content
    ----------------------------------------------------------------------------------------
    Item Name: Vuesax Admin - VueJS Dashboard Admin Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div class="domain-management">
    <div class="vx-row">
      <div class="vx-col w-full mb-base">
        <p>Found {{domainCount}} unique domains in the content. You can label them below:</p>
      </div>
    </div>

    <div class="table-wrapper">
      <vs-table :data="domainMappings" class="domain-table">
        <template slot="thead">
          <vs-th>Domain</vs-th>
          <vs-th>Label</vs-th>
          <vs-th>Version</vs-th>
          <vs-th>Description</vs-th>
          <vs-th>Internal Note</vs-th>
        </template>
        
        <template slot-scope="{data}">
          <vs-tr v-for="(tr, index) in data" :key="index">
            <vs-td>{{tr.domain}}</vs-td>
            <vs-td>
              <vs-input v-model="tr.label" placeholder="e.g. Client 1 Localhost" />
            </vs-td>
            <vs-td>
              <vs-select v-model="tr.version">
                <vs-select-item value="Live" text="Live" />
                <vs-select-item value="Test" text="Test" />
                <vs-select-item value="Preview" text="Preview" />
                <vs-select-item value="Training" text="Training" />
                <vs-select-item value="Dev" text="Dev" />
              </vs-select>
            </vs-td>
            <vs-td>
              <vs-input v-model="tr.description" placeholder="Description for user (optional)" />
            </vs-td>
            <vs-td>
              <vs-input v-model="tr.internalNote" placeholder="Internal note (optional)" />
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </div>

    <div class="vx-row mt-4">
      <div class="vx-col w-full">
        <vs-button @click="applyMappings" color="primary" type="filled" native-type="button">
          Apply Labels
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

export default {
  name: 'DomainManagement',
  props: {
    rawContent: {
      type: String,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      domainMappings: [],
      domainCount: 0
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchAndMergeDomains();
      }
    },
    rawContent(newVal) {
      if (this.visible) {
        this.fetchAndMergeDomains();
      }
    }
  },
  methods: {
    extractDomainsFromContent(content) {
      const domains = new Set();
      if (!content) {
        console.log('No content to extract domains from');
        return domains;
      }

      try {

        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const stepBlocks = doc.querySelectorAll('[data-type="draggable-item"]');
        
        stepBlocks.forEach((block, index) => {
          const textContent = block.innerText;
          
          const urlMatches = textContent.match(/"url"\s*:\s*"([^"]+)"/g);
          if (urlMatches) {
            urlMatches.forEach(match => {
              const urlMatch = match.match(/"url"\s*:\s*"([^"]+)"/);
              if (urlMatch) {
                try {
                  const url = urlMatch[1];
                  const domain = new URL(url).host;
                  domains.add(domain);
                } catch (e) {
                  console.warn('Invalid URL:', urlMatch[1], e);
                }
              }
            });
          }
        });

        console.log('Final domain set:', Array.from(domains));
        return domains;
      } catch (error) {
        console.error('Error extracting domains:', error);
        return domains;
      }
    },

    updateDomainMappings() {
      const domains = this.extractDomainsFromContent(this.rawContent);
      this.domainCount = domains.size;
    },

    async fetchAndMergeDomains() {
      const domains = Array.from(this.extractDomainsFromContent(this.rawContent)).map(d => d.toLowerCase());
      console.log('Extracted domains:', domains);
      if (!domains.length) return;
      const query = `
        query OnboardingDomains($where: OnboardingDomainFilterInput) {
          onboardingDomains(where: $where) {
            id
            domain
            label
            version
            icon
          }
        }
      `;
      const variables = { where: { domain: { in: domains } } };
      const req = { query, variables };
      try {
        const response = await TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        });
        const knownDomains = (response.data.data && response.data.data.onboardingDomains) || [];
        this.domainMappings = domains.map(domain => {
          const found = knownDomains.find(d => d.domain === domain);
          return found ? { ...found } : { domain, label: '', version: '', icon: '' };
        });
        console.log('domainMappings:', this.domainMappings);
      } catch (e) {
        this.domainMappings = domains.map(domain => ({ domain, label: '', version: '', icon: '' }));
        console.log('domainMappings (fallback):', this.domainMappings);
      }
    },

    replaceDomainsWithLabels(content, mappings) {
      let updatedContent = content;
      mappings.forEach(mapping => {
        if (mapping.label) {
          const regex = new RegExp(`https?://(${mapping.domain.replace(/\./g, '\\.')})`, 'g');
          updatedContent = updatedContent.replace(regex, `:::${mapping.label}:::`);
        }
      });
      return updatedContent;
    },

    validateLabels() {
      for (const mapping of this.domainMappings) {
        if (mapping.label && !/^[A-Za-z0-9-]+$/.test(mapping.label)) {
          this.$vs.notify({
            title: "Invalid Label",
            text: "Labels can only contain letters, numbers, and hyphens.",
            color: "danger",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-alert-circle",
          });
          return false;
        }
      }
      return true;
    },

    applyMappings() {
      console.log('applyMappings called');
      if (!this.validateLabels()) return;
      const mappings = this.domainMappings.map(m => ({
        id: m.id || 0,
        domain: m.domain,
        label: m.label,
        version: m.version,
        icon: m.icon || '',
        description: m.description || '',
        internalNote: m.internalNote || ''
      }));

      const query = `
        mutation AddOrUpdateOnboardingDomain($input: OnboardingDomainInput!) {
          addOrUpdateOnboardingDomain(input: $input) {
            id
            domain
            label
            version
            icon
          }
        }
      `;

      Promise.all(mappings.map(mapping => {
        const variables = { input: mapping };
        const req = { query, variables };
        return TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        });
      })).then(() => {
        const updatedContent = this.replaceDomainsWithLabels(this.rawContent, mappings);
        this.$emit('content-updated', updatedContent);
      }).catch(() => {
        this.$vs.notify({
          title: "Error!",
          text: "There was a problem saving domain labels.",
          color: "danger",
          iconPack: "feather",
          position: "top-center",
          icon: "icon-alert-circle",
        });
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.domain-management {
  padding: 1rem;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.domain-table {
  width: 100% !important;
  min-width: 600px;
  table-layout: auto;
}

.domain-table th, .domain-table td {
  white-space: nowrap;
}
</style> 
